import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getVocabulary } from "../services/examService";
import styles from "./Study/Spanish/SpanishExam.module.css";

const getFullPos = (pos) => {
  if (!pos) return "Noun";

  const cleanPos = pos.toString().trim().toLowerCase();
  const map = {
    n: "Noun",
    "n.": "Noun",
    noun: "Noun",
    v: "Verb",
    "v.": "Verb",
    verb: "Verb",
    adj: "Adjective",
    "adj.": "Adjective",
    adjective: "Adjective",
    adv: "Adverb",
    "adv.": "Adverb",
    adverb: "Adverb",
    phr: "Phrase",
    "phr.": "Phrase",
    phrase: "Phrase",
    prep: "Preposition",
    "prep.": "Preposition",
    preposition: "Preposition",
    conj: "Conjunction",
    "conj.": "Conjunction",
    conjunction: "Conjunction",
    pron: "Pronoun",
    "pron.": "Pronoun",
    pronoun: "Pronoun",
  };

  return map[cleanPos] || cleanPos.charAt(0).toUpperCase() + cleanPos.slice(1);
};

function Nav({ active, mobile, setUser, user, vocabularyOnly = false, hideVocabulary = false }) {
  const navigate = useNavigate();
  const [openMenu, setOpenMenu] = useState(null);
  const [vocabOpen, setVocabOpen] = useState(false);
  const [vocabItems, setVocabItems] = useState([]);
  const [vocabLoading, setVocabLoading] = useState(false);
  const [vocabSearch, setVocabSearch] = useState("");
  const [expandedVocabularyId, setExpandedVocabularyId] = useState(null);
  const vocabRef = useRef(null);

  const links = [
    { id: "home", label: "Home", icon: "bi bi-house-fill", path: "/" },
    {
      id: "study",
      label: "Study",
      icon: "bi bi-mortarboard",
      path: "/study"
    },
    {
      id: "projects",
      label: "Projects",
      icon: "bi bi-box-seam",
      path: "/projects",
    },
    {
      id: "contacts",
      label: "Contacts",
      icon: "bi bi-people-fill",
      path: "/contacts",
    },
  ];


  const handleClick = (link) => {
    if (link.subLinks) {
      setOpenMenu(openMenu === link.id ? null : link.id);
    } else {
      setOpenMenu(null);
      navigate(link.path);
    }
  };

  useEffect(() => {
    if (!vocabOpen) return;

    let ignore = false;
    (async () => {
      setVocabLoading(true);
      try {
        const data = await getVocabulary("spanish");
        if (!ignore) setVocabItems(data || []);
      } catch (err) {
        console.error(err);
      } finally {
        if (!ignore) setVocabLoading(false);
      }
    })();

    return () => {
      ignore = true;
    };
  }, [vocabOpen]);

  useEffect(() => {
    if (!vocabOpen) return;

    const handleClickOutside = (event) => {
      if (vocabRef.current && !vocabRef.current.contains(event.target)) {
        setVocabOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [vocabOpen]);

  const filteredVocabulary = useMemo(() => {
    const query = vocabSearch.trim().toLowerCase();
    const sortedItems = [...vocabItems].sort((a, b) => {
      const aText = String(a.spanish || "").toLowerCase();
      const bText = String(b.spanish || "").toLowerCase();
      return aText.localeCompare(bText);
    });

    if (!query) return sortedItems;

    return sortedItems.filter((item) => {
      const haystack = [item.spanish, item.english, item.chinese, item.contextSentence]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [vocabItems, vocabSearch]);

  const toggleVocabulary = () => {
    setOpenMenu(null);
    setVocabOpen((prev) => !prev);
  };

  const dropdownTransform = mobile ? "translateX(-50%)" : "translateX(0)";

  return (
    <nav
      className={
        vocabularyOnly
          ? "vocabulary-floating-nav"
          : mobile
            ? "card nav-card w-full max-w-full px-2 py-1 overflow-visible"
            : "card relative flex-1 flex flex-col"
      }
    >
      <ul
        className={
          vocabularyOnly
            ? "vocabulary-floating-list"
            : mobile
              ? `flex flex-row flex-wrap items-center w-full overflow-visible ${user && user.isAdmin
                ? "justify-center gap-[4px]"
                : "justify-start gap-[12px]"
              }`
              : "nav nav-pills flex-column mb-auto relative"
        }
        style={vocabularyOnly ? {} : mobile ? { flexWrap: "wrap" } : {}}
      >
        {!vocabularyOnly && links.map((link) => (
          <li
            key={link.id}
            className={mobile ? "flex-shrink-0 relative" : "nav-item relative"}
            style={mobile ? { maxWidth: "6vw", maxHeight: "40px", zIndex: openMenu === link.id ? 50 : 1 } : { zIndex: openMenu === link.id ? 50 : 1 }}
          >
            <button
              onClick={() => handleClick(link)}
              className={`nav-link transition-all duration-300 transform 
    ${mobile
                  ? "p-2 min-w-0 flex flex-col items-center justify-center bg-gray-150 rounded-full"
                  : "text-left w-full"
                } 
    ${active === link.id || openMenu === link.id
                  ? "translate-x-1 bg-white/40 backdrop-blur-2xl border border-white/30 shadow-inner"
                  : "hover:bg-white/30 hover:scale-110 hover:shadow-md"
                }`}
              style={
                mobile
                  ? {
                    width: "40px",
                    height: "40px",
                    minWidth: 0,
                    overflow: "hidden",
                    padding: 0,
                    margin: "2px",
                  }
                  : {}
              }
            >
              <span
                className={
                  mobile
                    ? "flex justify-center mt-4 items-center w-full h-full"
                    : ""
                }
                style={
                  mobile
                    ? {
                      height: "40px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }
                    : {}
                }
              >
                <i
                  className={`${link.icon}`}
                  style={
                    mobile
                      ? {
                        fontSize: "1.25em",
                        margin: "0",
                        display: "inline-block",
                        lineHeight: "40px",
                        verticalAlign: "middle",
                      }
                      : {}
                  }
                ></i>
              </span>
              <span className={mobile ? "block text-xs text-center" : "ml-1"}>
                {link.label}
              </span>
            </button>

            {/* Sub-menu Popup */}
            {link.subLinks && openMenu === link.id && (
              <div
                className={
                  mobile
                    ? "absolute top-full left-0 mt-2 bg-white dark:bg-[var(--dark-background)] shadow-xl rounded-lg flex flex-col p-2 min-w-[120px] border border-gray-200 dark:border-gray-700"
                    : "absolute left-full top-0 ml-2 bg-white dark:bg-[var(--dark-background)] shadow-xl rounded-lg flex flex-col p-2 min-w-[140px] border border-gray-200 dark:border-gray-700"
                }
              >
                {link.subLinks.map(sub => (
                  <button
                    key={sub.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(sub.path);
                      setOpenMenu(null);
                    }}
                    className="text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-[var(--primary)] rounded-md transition-colors text-sm font-medium"
                  >
                    {sub.label}
                  </button>
                ))}
              </div>
            )}
          </li>
        ))}
        {!hideVocabulary && <li
          className={vocabularyOnly ? "vocabulary-floating-item relative" : mobile ? "flex-shrink-0 relative ml-auto" : "nav-item relative"}
          ref={vocabRef}
          style={vocabularyOnly ? { zIndex: vocabOpen ? 60 : 1 } : mobile ? { marginLeft: "auto", zIndex: vocabOpen ? 60 : 1 } : { zIndex: vocabOpen ? 60 : 1 }}
        >
          <button
            onClick={toggleVocabulary}
            className={`${vocabularyOnly ? "vocabulary-floating-button" : "nav-link transition-all duration-300 transform " + (mobile ? "p-2 min-w-0 flex flex-col items-center justify-center bg-gray-150 rounded-full" : "text-left w-full")} ${vocabOpen ? "translate-x-1 bg-white/40 backdrop-blur-2xl border border-white/30 shadow-inner" : "hover:bg-white/30 hover:scale-110 hover:shadow-md"}`}
            style={vocabularyOnly ? {} : mobile ? { width: "40px", height: "40px", minWidth: 0, overflow: "hidden", padding: 0, margin: "2px" } : {}}
          >
            <span className={vocabularyOnly ? "" : mobile ? "flex justify-center mt-4 items-center w-full h-full" : ""} style={vocabularyOnly ? {} : mobile ? { height: "40px", display: "flex", alignItems: "center", justifyContent: "center" } : {}}>
              <i className="bi bi-bookmarks-fill" style={!vocabularyOnly && mobile ? { fontSize: "1.1em", margin: "0", display: "inline-block", lineHeight: "40px", verticalAlign: "middle" } : {}}></i>
            </span>
            {/* <span className={vocabularyOnly ? "vocabulary-floating-label" : mobile ? "block text-xs text-center" : "ml-1"}>Vocab</span> */}
          </button>

          <div
            className={`${styles.vocabularyTab}`}
            style={{
              position: "absolute",
              top: mobile ? "calc(100% + 0.5rem)" : "calc(100% + 0.5rem)",
              left: vocabularyOnly ? "8px" : mobile ? "50%" : "0",
              zIndex: 70,
              minWidth: mobile ? "280px" : "320px",
              maxWidth: mobile ? "92vw" : "320px",
              maxHeight: mobile ? "70vh" : "70vh",
              overflow: "hidden",
              background: "var(--color-bg-card, #fff)",
              borderRadius: "12px",
              border: "1px solid rgba(226, 232, 240, 0.9)",
              boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
              padding: "0.5rem",
              opacity: vocabOpen ? 1 : 0,
              transform: vocabOpen ? `${vocabularyOnly ? "translateX(0)" : dropdownTransform} translateY(0) scale(1)` : `${vocabularyOnly ? "translateX(0)" : dropdownTransform} translateY(-6px) scale(0.98)`,
              pointerEvents: vocabOpen ? "auto" : "none",
              transition: "opacity 220ms ease, transform 220ms ease, max-height 220ms ease",
              transformOrigin: "top center",
            }}
          >
            {vocabOpen && (
              <>
                <div className={styles.vocabularyToolbar}>
                  <span className={styles.vocabularyCount}>{filteredVocabulary.length} words</span>
                  <input
                    value={vocabSearch}
                    onChange={(e) => setVocabSearch(e.target.value)}
                    placeholder="Search by word or phrase"
                    className={styles.vocabularySearch}
                  />
                </div>

                <div className={styles.vocabularyList} style={{ maxHeight: mobile ? "55vh" : "60vh", overflowY: "auto", paddingRight: "0.1rem" }}>
                  {vocabLoading ? (
                    <div className="px-2 py-3 text-sm text-gray-500">Loading vocabulary…</div>
                  ) : filteredVocabulary.length === 0 ? (
                    <div className="px-2 py-3 text-sm text-gray-500">No matching vocabulary yet.</div>
                  ) : (
                    filteredVocabulary.map((item) => {
                      const posValue = item.pos || item.partOfSpeech || item.type;
                      return (
                        <div
                          key={item.id}
                          onClick={() => setExpandedVocabularyId((current) => (current === item.id ? null : item.id))}
                          className={`${styles.vocabularyCard} ${expandedVocabularyId === item.id ? styles.vocabularyCardExpanded : ""}`}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              setExpandedVocabularyId((current) => (current === item.id ? null : item.id));
                            }
                          }}
                        >
                          <div className={styles.vocabularyHeader}>
                            <span className={styles.vocabularySpanish}>{item.spanish || item.word}</span>
                            <span className={styles.vocabularyPos}>{getFullPos(posValue)}</span>
                          </div>

                          <div className={`${styles.vocabularyDetails} ${expandedVocabularyId === item.id ? styles.vocabularyDetailsOpen : ""}`}>
                            <div style={{ overflow: "hidden" }}>
                              <div className={styles.vocabularyDetailGrid}>
                                <div>
                                  <span className={styles.vocabularyLabel}>ENGLISH</span>
                                  <p>{item.english || item.meaning || "N/A"}</p>
                                </div>
                                <div>
                                  <span className={styles.vocabularyLabel}>CHINESE</span>
                                  <p>{item.chinese || item.translation || "暂无中文翻译"}</p>
                                </div>
                              </div>

                              {item.contextSentence && (
                                <div className={styles.vocabularyContextBox}>
                                  <span className={styles.vocabularyLabel}>CONTEXT</span>
                                  <p>“{item.contextSentence}”</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </div>
        </li>}

        {!vocabularyOnly && mobile && user?.isAdmin && (
          <li style={{ maxWidth: "12vw", maxHeight: "40px", flex: "1 1 0" }}>
            <button
              onClick={() => {
                if (setUser) setUser(null);
                localStorage.removeItem("user");
                navigate("/");
              }}
              className="nav-link p-2 min-w-0 flex flex-col items-center justify-center bg-gray-150 rounded-full"
              style={{
                width: "40px",
                height: "40px",
                minWidth: 0,
                overflow: "hidden",
                padding: 0,
                margin: "2px",
              }}
            >
              <span
                className="flex justify-center mt-4 items-center w-full h-full"
                style={{
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <i
                  className="bi bi-box-arrow-right"
                  style={{
                    fontSize: "1.25em",
                    margin: "0",
                    display: "inline-block",
                    lineHeight: "40px",
                    verticalAlign: "middle",
                  }}
                ></i>
              </span>
              <span className="block text-xs text-center">Logout</span>
            </button>
          </li>
        )}
      </ul>


    </nav>
  );
}

export default Nav;

